import { useEffect, useMemo, useState } from 'react';
import { getText } from 'i18n/intl';
import { keyBy } from 'lodash';
import { fieldsSelector } from 'slices/field.slice';
import { selectActivityCreateTemplates } from 'slices/objectGenerationTemplate.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  EActivityDraftGroupConfigActivityProperty,
  EActivityDraftGroupConfigFieldOptions,
  EActivityDraftGroupConfigNumberingType,
  type TActivityDraftGroupConfig,
  type TActivityDraftGroupConfigFieldOptionsValuesSchema,
} from '@timeedit/types/lib/types/activityGroup.type';
import { Icon, TEInput } from '@timeedit/ui-components';
import {
  Button,
  Dropdown,
  type MenuProps,
  Modal,
} from '@timeedit/ui-components/antd';
import { fieldOrNamingValueToString } from '../utils';

export const ObjectGenerationDropDown = ({
  onSelect,
  value,
  isMandatory,
  onDelete,
  rowId,
  children,
  useNumbering,
  currentTemplate,
}: {
  value?: TActivityDraftGroupConfigFieldOptionsValuesSchema;
  isMandatory?: boolean;
  onSelect: (
    typeAndValue: TActivityDraftGroupConfigFieldOptionsValuesSchema,
    rowIndex?: number
  ) => void;
  onDelete?: (rowId: number) => void;
  rowId?: number;
  children?: React.ReactNode;
  useNumbering?: boolean;
  currentTemplate: TActivityDraftGroupConfig;
}) => {
  const objectTypes = useAppSelector(selectObjectTypesList);
  const fields = useAppSelector(fieldsSelector);
  const activityCreateTemplates = useAppSelector(selectActivityCreateTemplates);
  const activityCreateTemplate = useMemo(() => {
    const currentACTId = currentTemplate.activityCreateTemplateId;
    return activityCreateTemplates.find((act) => act._id === currentACTId);
  }, [currentTemplate.activityCreateTemplateId, activityCreateTemplates]);

  const objectTypesAndFieldsOnCurrentActivityCreateTemplate: {
    objectTypesToSelect: { value: number; label: string }[];
    fieldsToSelectPerObjectType: {
      [objectTypeId: number]: { value: number; label: string }[];
    };
  } = useMemo(() => {
    if (!activityCreateTemplate)
      return { objectTypesToSelect: [], fieldsToSelectPerObjectType: {} };

    const objectExtIdsOnACT = [
      activityCreateTemplate.primaryObject.extId,
      activityCreateTemplate.activityType.extId,
      ...activityCreateTemplate.objects.map((obj) => obj.extId),
    ];

    const objectTypesOnActivityTemplate = objectTypes.filter((objType) =>
      objectExtIdsOnACT.includes(objType.extId)
    );

    const fieldsIndexed = keyBy(fields, 'id');
    const fieldsToSelectPerObjectType = objectTypesOnActivityTemplate.reduce(
      (acc, objType) => {
        const objectTypeFields = objType.fields
          .map((fieldId) => fieldsIndexed[fieldId])
          .filter((f) => f !== undefined)
          .map((f) => ({
            value: f!.id,
            label: f!.name,
          }));
        return { ...acc, [objType.id]: objectTypeFields };
      },
      {} as {
        [objectTypeId: number]: { value: number; label: string }[];
      }
    );

    return {
      objectTypesToSelect: objectTypesOnActivityTemplate.map((obj) => ({
        value: obj.id,
        label: obj.name,
      })),
      fieldsToSelectPerObjectType,
    };
  }, [objectTypes, activityCreateTemplate, fields]);

  const selectedActivityFields = useMemo(() => {
    const activityCreateTemplateFields = activityCreateTemplate?.fields || [];
    return fields.filter((field) =>
      activityCreateTemplateFields.some((af) => af.extId === field.extId)
    );
  }, [activityCreateTemplate, fields]);
  const informationFields = useMemo(() => {
    return (
      activityCreateTemplate?.informationFields.map((f) => ({
        label: `${f.label} (Information Field)`,
        key: `${EActivityDraftGroupConfigFieldOptions.ACTIVITY_FIELD_VALUE}-${f.label}`,
      })) || []
    );
  }, [activityCreateTemplate?.informationFields]);
  const [visible, setVisible] = useState(false);
  const [openStaticTextModal, setOpenStaticTextModal] = useState(false);
  const [staticText, setStaticText] = useState('');

  const [openObjectAndFieldSelectorModal, setOpenObjectAndFieldSelectorModal] =
    useState(false);
  const [currentObjectTypeId, setCurrentObjectTypeId] = useState<number | null>(
    null
  );
  const [currentFieldId, setCurrentFieldId] = useState<number | null>(null);

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    if (e.key === EActivityDraftGroupConfigFieldOptions.STATIC_TEXT) {
      setOpenStaticTextModal(true);
      return;
    }
    if (e.key === EActivityDraftGroupConfigFieldOptions.OBJECT_FIELD_VALUE) {
      setOpenObjectAndFieldSelectorModal(true);
      return;
    }

    const [type, value] = e.key.split('-');
    onSelect({
      type: type as EActivityDraftGroupConfigFieldOptions.ACTIVITY_FIELD_VALUE,
      value,
    });
    setVisible(false);
  };

  const onAddOrEditStaticText = () => {
    if (value && rowId) {
      onSelect(
        {
          type: EActivityDraftGroupConfigFieldOptions.STATIC_TEXT,
          value: staticText,
        },
        rowId
      );
      setOpenStaticTextModal(false);
      setStaticText('');
      return;
    }
    onSelect({
      type: EActivityDraftGroupConfigFieldOptions.STATIC_TEXT,
      value: staticText,
    });

    setOpenStaticTextModal(false);
    setStaticText('');
  };

  const onChangeTypeAndField = () => {
    if (currentObjectTypeId === null && currentFieldId === null) {
      setOpenObjectAndFieldSelectorModal(false);
      return;
    }

    if (value && rowId) {
      onSelect(
        {
          type: EActivityDraftGroupConfigFieldOptions.OBJECT_FIELD_VALUE,
          fieldIntId: currentFieldId!,
          objectTypeIntId: currentObjectTypeId!,
        },
        rowId
      );
      setOpenObjectAndFieldSelectorModal(false);
      setCurrentObjectTypeId(null);
      setCurrentFieldId(null);
      return;
    }
    onSelect({
      type: EActivityDraftGroupConfigFieldOptions.OBJECT_FIELD_VALUE,
      fieldIntId: currentFieldId!,
      objectTypeIntId: currentObjectTypeId!,
    });

    setOpenObjectAndFieldSelectorModal(false);
    setCurrentObjectTypeId(null);
    setCurrentFieldId(null);
  };

  const defaultMenu: MenuProps['items'] = useMemo(() => {
    return [
      {
        label: getText('static_text'),
        key: EActivityDraftGroupConfigFieldOptions.STATIC_TEXT,
      },
      {
        label: getText('object_field_value'),
        key: EActivityDraftGroupConfigFieldOptions.OBJECT_FIELD_VALUE,
      },
      {
        label: getText('activity_field_value'),
        key: 'activity-type',
        children: [
          ...selectedActivityFields.map((field) => ({
            label: field.name,
            key: `${EActivityDraftGroupConfigFieldOptions.ACTIVITY_FIELD_VALUE}-${field.extId}`,
          })),
          ...informationFields,
        ],
      },
      {
        label: getText('activity_property'),
        key: 'activity-property',
        children: Object.values(EActivityDraftGroupConfigActivityProperty).map(
          (prop) => ({
            label:
              getText(`activity_group_act_prop_${prop.toLowerCase()}`) || prop,
            key: `${EActivityDraftGroupConfigFieldOptions.ACTIVITY_PROPERTY}-${prop}`,
          })
        ),
      },
    ];
  }, [selectedActivityFields, informationFields]);

  const menu = useMemo(() => {
    if (useNumbering) {
      return [
        ...defaultMenu,
        {
          label: getText('numbering'),
          key: 'numbering',
          children: [
            {
              label: 'ABC',
              key: `${EActivityDraftGroupConfigFieldOptions.NUMBERING}-${EActivityDraftGroupConfigNumberingType.ALPHABETIC}`,
            },
            {
              label: '123',
              key: `${EActivityDraftGroupConfigFieldOptions.NUMBERING}-${EActivityDraftGroupConfigNumberingType.NUMERIC}`,
            },
          ],
        },
      ];
    }
    return [...defaultMenu];
  }, [useNumbering, defaultMenu]);

  const displayValue = useMemo(() => {
    return fieldOrNamingValueToString(value, fields, objectTypes);
  }, [value, fields, objectTypes]);

  useEffect(() => {
    if (!value) {
      setStaticText('');
      setCurrentFieldId(null);
      setCurrentObjectTypeId(null);
      return;
    }
    const { type } = value;
    if (type === EActivityDraftGroupConfigFieldOptions.STATIC_TEXT) {
      setStaticText(value.value);
      return;
    }
    if (type === EActivityDraftGroupConfigFieldOptions.OBJECT_FIELD_VALUE) {
      setCurrentObjectTypeId(value.objectTypeIntId);
      setCurrentFieldId(value.fieldIntId);
      return;
    }
  }, [value]);

  return (
    <div>
      <Dropdown
        menu={{ items: menu, onClick: handleMenuClick }}
        trigger={['click']}
        onOpenChange={setVisible}
        open={visible}
      >
        {children ? (
          children
        ) : (
          <div className={'te-flex'}>
            <div style={{ width: '90%' }}>
              <Button className={'te-w-full'}>
                <div className="te-flex te-justify-between te-items-center te-w-full">
                  <span>
                    {' '}
                    {displayValue
                      ? displayValue
                      : getText('select_field_placeholder')}
                  </span>
                  <Icon id="DownOutlined" alt="Down" />
                </div>
              </Button>
            </div>
            {!isMandatory && (
              <div className={'te-ml-2'}>
                <Button className={'te-w-full'}>
                  <Icon
                    id="DeleteOutlined"
                    alt="Delete"
                    onClick={() => onDelete(rowId)}
                  />
                </Button>
              </div>
            )}
          </div>
        )}
      </Dropdown>
      <Modal
        title={`${getText('add')} ${getText('static_text')}`}
        open={openStaticTextModal}
        onOk={() => onAddOrEditStaticText()}
        onCancel={() => {
          setOpenStaticTextModal(false);
          setStaticText('');
        }}
      >
        <TEInput.Input
          value={staticText}
          placeholder={getText('static_text')}
          onChange={setStaticText}
        />
      </Modal>

      <Modal
        title={getText('select_object_type_and_field')}
        open={openObjectAndFieldSelectorModal}
        onOk={() => onChangeTypeAndField()}
        okButtonProps={{
          disabled: currentObjectTypeId === null || currentFieldId === null,
        }}
        onCancel={() => {
          setCurrentObjectTypeId(null);
          setCurrentFieldId(null);
          setOpenObjectAndFieldSelectorModal(false);
        }}
      >
        <div className="te-mt-2">
          <TEInput.Select
            options={
              objectTypesAndFieldsOnCurrentActivityCreateTemplate.objectTypesToSelect
            }
            label={getText('objects_type')}
            tooltip={getText('objects_type_on_activity_template')}
            value={currentObjectTypeId}
            onChange={(selectObjectId: unknown) => {
              setCurrentObjectTypeId(
                typeof selectObjectId === 'number' && !isNaN(selectObjectId)
                  ? selectObjectId
                  : null
              );
              setCurrentFieldId(null);
            }}
          />
        </div>
        <div style={{ marginTop: 10 }}>
          {currentObjectTypeId === null ? (
            <>{getText('select_object_type_first')}</>
          ) : (
            <TEInput.Select
              options={
                objectTypesAndFieldsOnCurrentActivityCreateTemplate
                  .fieldsToSelectPerObjectType[currentObjectTypeId] ?? []
              }
              label={getText('select_field')}
              tooltip={getText('select_field_on_object')}
              value={currentFieldId}
              onChange={(selectFieldId: unknown) => {
                setCurrentFieldId(
                  typeof selectFieldId === 'number' && !isNaN(selectFieldId)
                    ? selectFieldId
                    : null
                );
              }}
            ></TEInput.Select>
          )}
        </div>
      </Modal>
    </div>
  );
};
